import { useEffect, useState } from "react";
import "./WeeklyWaterAndWeather.css"; // Stylesheet for report styling

// Importing individual report sections
import RiverFlowsAndLevels from "./RiverFlowsAndLevels";
import CoverPage from "./CoverPage";
import AboutPage from "./AboutPage";
import TableOfContents from "./TableOfContents";
import BarragesSummary from "./BarragesSummary";
import RimsSummary from "./RimsSummary";
import DamsSummary from "./DamsSummary";
import SynopticSection from "./SynopticSection";

const Data = () => {
  // State variables to hold fetched data
  const [textData, setTextData] = useState(null);
  const [riverFlows, setRiverFlows] = useState([]);
  const [barrages, setBarrages] = useState([]);
  const [dams, setDams] = useState([]);
  const [rims, setRims] = useState([]);
  // For capturing fetch errors

  // Extract query parameters from URL
  const params = new URLSearchParams(window.location.search);
  const startDate = params.get("startDate");
  const endDate = params.get("endDate");
  const publishDate = params.get("publishDate");

  // useEffect to fetch data once the component mounts
  useEffect(() => {
    fetchAllData();
  }, []);

  // Fetch all required datasets in parallel using Promise.all
  const fetchAllData = async () => {
    try {
      const [textRes, riverRes, barrageRes, damRes, rimRes] = await Promise.all(
        [
          fetch(
            `${process.env.REACT_APP_API_URL}/dynamicReports/get-text-data?startDate=${startDate}&endDate=${endDate}&publishDate=${publishDate}`
          ),
          fetch(
            `${process.env.REACT_APP_API_URL}/dynamicReports/river-flows-and-levels?startDate=${startDate}&endDate=${endDate}`
          ),
          fetch(
            `${process.env.REACT_APP_API_URL}/dynamicReports/barrages?startDate=${startDate}&endDate=${endDate}`
          ),
          fetch(
            `${process.env.REACT_APP_API_URL}/dynamicReports/dams?startDate=${startDate}&endDate=${endDate}`
          ),
          fetch(
            `${process.env.REACT_APP_API_URL}/dynamicReports/rims?startDate=${startDate}&endDate=${endDate}`
          ),
        ]
      );

      // Parse JSON responses
      const [textData, riverData, barrageData, damData, rimData] =
        await Promise.all([
          textRes.json(),
          riverRes.json(),
          barrageRes.json(),
          damRes.json(),
          rimRes.json(),
        ]);

      // Update state with fetched data
      setTextData(textData);
      setRiverFlows(riverData.data);
      setBarrages(barrageData.data);
      setDams(damData.data);
      setRims(rimData.data);
    } catch (err) {
      console.error("Failed to fetch report data:", err);
    }
  };

  // Generates week descriptions (current and previous) based on input dates
  const getWeekDescription = (startDate, endDate) => {
    // Helper function to get ISO week number
    const getWeekNumber = (date) => {
      const d = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
      );
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    };

    // Helper function to format date as "27 April"
    const formatDate = (date) => {
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
      });
    };

    // Convert input dates to Date objects
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Calculate previous week's start and end dates
    const prevStart = new Date(start);
    const prevEnd = new Date(end);
    prevStart.setDate(prevStart.getDate() - 7);
    prevEnd.setDate(prevEnd.getDate() - 7);

    // Compute week numbers
    const currentWeekNo = getWeekNumber(start) + 1;
    const previousWeekNo = getWeekNumber(prevStart) + 1;

    // Return formatted week descriptions
    return {
      currentWeek: `${currentWeekNo}th Week Average\n${formatDate(
        start
      )} to ${formatDate(end)} ${end.getFullYear()}`,
      previousWeek: `${previousWeekNo}th Week Average\n${formatDate(
        prevStart
      )} to ${formatDate(prevEnd)} ${prevEnd.getFullYear()}`,
      weekVsWeek: `Week ${currentWeekNo} vs Week ${previousWeekNo}`,
      currentWeekName: `Week ${currentWeekNo}`,
      previousWeekName: `Week ${previousWeekNo}`,
    };
  };

  // Get computed week labels
  const weekDescription = getWeekDescription(startDate, endDate);

  return (
    <div>
      {/* Cover Page with publication date */}
      <CoverPage date={publishDate} />

      <div>
        {/* Static and dynamic sections of the report */}
        <AboutPage />
        <TableOfContents />
        <SynopticSection data={textData} />
        <RiverFlowsAndLevels data={riverFlows} />
        <BarragesSummary
          data={barrages}
          currentWeekLabel={weekDescription.currentWeek}
          previousWeekLabel={weekDescription.previousWeek}
          weekVsWeekLabel={weekDescription.weekVsWeek}
          currentWeek={weekDescription.currentWeekName}
          previousWeek={weekDescription.previousWeekName}
        />
        <RimsSummary
          data={rims}
          currentWeekLabel={weekDescription.currentWeek}
          previousWeekLabel={weekDescription.previousWeek}
          weekVsWeekLabel={weekDescription.weekVsWeek}
          currentWeek={weekDescription.currentWeekName}
          previousWeek={weekDescription.previousWeekName}
        />

        <DamsSummary
          data={dams}
          currentWeekLabel={weekDescription.currentWeek}
          previousWeekLabel={weekDescription.previousWeek}
          endDate={endDate}
          tarbelaLevels={riverFlows[0]?.["Indus@Tarbela"]?.levels?.value}
          manglaLevels={riverFlows[0]?.["Jhelum@Mangla"]?.levels?.value}
        />
      </div>
    </div>
  );
};

export default Data;
